import {
  Bullseye,
  Button,
  Divider,
  EmptyState,
  EmptyStateIcon,
  FileUpload,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  PageSection,
  Spinner,
  Title,
} from '@patternfly/react-core'
import {
  CheckIcon,
  ErrorCircleOIcon,
  SearchIcon,
} from '@patternfly/react-icons'
import {
  Table,
  TableHeader,
  TableBody,
  TableVariant,
} from '@patternfly/react-table'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isIntegration } from '@app/domain/integration/integration.helpers'
import type { IIntegration } from '@app/domain/integration/integration.interface'
import { parseFromString } from '@app/lib/parser'
import { hasContent } from '@app/lib/string'
import type { AppState } from '@app/state'
import * as Clinic from '@app/state/clinic'
import type { IApiIntegration } from '@app/state/clinic/interfaces'

export const IntegrationsScreen = () => {
  const dispatch = useDispatch()

  const [value, setValue] = useState('')
  const [filename, setFilename] = useState('')
  const [parsedResult, setParsedResult] = useState<IIntegration | undefined>(
    undefined,
  )

  const [isLoading, setIsLoading] = useState(false)
  const [isRejected, setIsRejected] = useState(false)
  const [validationError, setValidationError] = useState(false)

  const initializState = () => {
    setValue('')
    setFilename('')
    setIsLoading(false)
    setIsRejected(false)
    setValidationError(false)
  }

  const integrations = useSelector<AppState, IApiIntegration[]>(
    (state) => state.clinic.integrations,
  )

  const updateStatus = useSelector<AppState, Clinic.ActionStatus>(
    (state) => state.clinic.integrationUpdateStatus,
  )

  const convertIntegrationToRow = (i: IApiIntegration) => {
    return {
      cells: [{ title: i.name }, { title: i.destination.uri }],
    }
  }

  const renderIntegrationTable = () => {
    const columnNames = [
      { title: 'Integration Name' },
      { title: 'Destination' },
    ]
    const rows = integrations.map(convertIntegrationToRow)
    return (
      <React.Fragment>
        {rows.length > 0 && (
          <Table
            aria-label="Existing Integrations"
            variant={TableVariant.compact}
            cells={columnNames}
            rows={rows}
          >
            <TableHeader />
            <TableBody />
          </Table>
        )}
        {rows.length === 0 && (
          <Bullseye>
            <EmptyState>
              <EmptyStateIcon icon={SearchIcon} />
              <Title headingLevel="h5" size="lg">
                No existing integrations found
              </Title>
            </EmptyState>
          </Bullseye>
        )}
      </React.Fragment>
    )
  }

  const handleFileChange = (
    newValue,
    newFilename,
    event: React.MouseEvent | React.DragEvent | React.ChangeEvent,
  ) => {
    if (event.nativeEvent instanceof MouseEvent && event.type === 'click') {
      initializState()
    } else if (hasContent(value)) {
      setValue(newValue)
      setFilename(newFilename)
      setValidationError(false)
      setIsRejected(false)

      const result = parseFromString(isIntegration)(value)
      if (result.hasError) {
        setValidationError(true)
        setParsedResult(undefined)
      } else {
        setValidationError(false)
        setParsedResult(result.parsed)
      }
    }
  }

  const handleFileRejected = useCallback(() => {
    setIsRejected(true)
  }, [])

  const handleFileReadStarted = useCallback(() => {
    setIsLoading(true)
  }, [])

  const handleFileReadFinished = useCallback(() => {
    setIsLoading(false)
  }, [])

  const handleFileSubmit = useCallback(() => {
    if (parsedResult) {
      dispatch(Clinic.actions.createIntegration(parsedResult))
    }
  }, [dispatch, parsedResult])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      handleFileSubmit()
    },
    [handleFileSubmit],
  )

  const disableSubmission = React.useMemo(
    () => validationError || isRejected || !hasContent(value),
    [isRejected, validationError, value],
  )

  const renderStatus = () => {
    if (updateStatus === Clinic.ActionStatus.Pending) {
      return (
        <div>
          <Spinner size="sm" />
        </div>
      )
    } else if (updateStatus === Clinic.ActionStatus.Succeeded) {
      return (
        <div>
          <CheckIcon size="sm" color="green" /> Integration created
          successfully!{' '}
        </div>
      )
    } else if (updateStatus === Clinic.ActionStatus.Failed) {
      return (
        <div>
          <ErrorCircleOIcon size="sm" color="red" /> Integration could not be
          created
        </div>
      )
    } else {
      return <div />
    }
  }

  return (
    <PageSection>
      <Title headingLevel="h1" size="xl">
        Data Integrations
      </Title>
      <Flex direction={{ default: 'column' }}>
        <FlexItem>{renderIntegrationTable()}</FlexItem>
        <FlexItem>
          <Divider />
        </FlexItem>
        <FlexItem>
          <Form onSubmit={handleSubmit}>
            <FormGroup
              fieldId="redcap-config-file-upload"
              helperText="Upload a JSON file"
              helperTextInvalid="Must be a valid JSON file that conforms to the configuration specification"
              validated={validationError || isRejected ? 'error' : 'default'}
            >
              <FileUpload
                id="integration-text-file"
                type="text"
                value={value}
                filename={filename}
                onChange={handleFileChange}
                onReadStarted={handleFileReadStarted}
                onReadFinished={handleFileReadFinished}
                isLoading={isLoading}
                dropzoneProps={{
                  accept: '.json',
                  onDropRejected: handleFileRejected,
                }}
                allowEditingUploadedText
                validated={validationError || isRejected ? 'error' : 'default'}
              />
            </FormGroup>
          </Form>
        </FlexItem>
        <FlexItem>
          <Button
            variant="primary"
            isDisabled={disableSubmission}
            onClick={() => handleFileSubmit()}
          >
            Create New Integration
          </Button>
          {renderStatus()}
        </FlexItem>
      </Flex>
    </PageSection>
  )
}
